import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Building2, Layers, Users, Plus, Trash2, CalendarClock, Save, Search } from 'lucide-react';
import { PageHeader } from '@/components/patterns';
import { AvatarDePersona } from '@/components/ui/avatar-de-persona';
import { nombreDelRol } from '@/lib/nombre-del-rol';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/lib/hooks/use-toast';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { organizationService } from '../services/organization-service';
import { masterDataService } from '@/features/master-data/services/master-data-service';
import { userService } from '@/features/users/services/user-service';
import type {
  Organization,
  OrganizationMember,
  OrganizationLOB,
} from '../types/organization.types';

interface LobOption {
  id: string;
  name: string;
  code: string;
  level: number;
}

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export function EquipoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  /*
    Borrar el equipo (18-oct). El servidor lo permitía (lo desactiva y sale de
    la lista) pero ninguna pantalla lo ofrecía: un equipo creado de más quedaba
    para siempre. Pregunta antes, como todo lo que no se deshace desde acá.
  */
  const borrarEquipo = (nombre: string) =>
    confirm({
      title: `¿Borrar el equipo ${nombre}?`,
      description:
        'Sale de la lista de equipos y ya no se le puede compartir una cotización. Las personas siguen en la empresa.',
      confirmText: 'Sí, borrar',
      variant: 'destructive',
      onConfirm: async () => {
        if (!id) return;
        try {
          await organizationService.delete(id);
          toast({ title: 'Equipo borrado', variant: 'success' });
          navigate('/configuracion/equipos');
        } catch (err: any) {
          toast({
            title: 'No se pudo borrar',
            description: err?.response?.data?.error || err?.message,
            variant: 'destructive',
          });
        }
      },
    });

  const [org, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [orgLobs, setOrgLobs] = useState<OrganizationLOB[]>([]);
  const [allLobs, setAllLobs] = useState<LobOption[]>([]);
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);

  // LOBs tab
  const [pendingLobIds, setPendingLobIds] = useState<Set<string>>(new Set());
  const [savingLobs, setSavingLobs] = useState(false);
  const [lobSearch, setLobSearch] = useState('');

  // Add member dialog (multi-select)
  const [addOpen, setAddOpen] = useState(false);
  const [pickedUserIds, setPickedUserIds] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [addValidUntil, setAddValidUntil] = useState('');
  const [addCoveringForId, setAddCoveringForId] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [o, m, l, masterLobs, users] = await Promise.all([
        organizationService.get(id),
        organizationService.listMembers(id),
        organizationService.listLobs(id),
        masterDataService.getLOBs(),
        userService.getUsers(),
      ]);
      setOrg(o);
      setMembers(m);
      setOrgLobs(l);
      setAllLobs(masterLobs as any);
      setAllUsers(users as any);
      setPendingLobIds(new Set(l.map((x) => x.id)));
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message || 'Error cargando equipo',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const lobIdsChanged = useMemo(() => {
    const current = new Set(orgLobs.map((l) => l.id));
    if (current.size !== pendingLobIds.size) return true;
    for (const x of current) if (!pendingLobIds.has(x)) return true;
    return false;
  }, [orgLobs, pendingLobIds]);

  const toggleLob = (lobId: string) => {
    setPendingLobIds((prev) => {
      const next = new Set(prev);
      if (next.has(lobId)) next.delete(lobId);
      else next.add(lobId);
      return next;
    });
  };

  const handleSaveLobs = async () => {
    if (!id) return;
    setSavingLobs(true);
    try {
      const next = await organizationService.setLobs(id, Array.from(pendingLobIds));
      setOrgLobs(next);
      toast({ title: 'LOBs actualizadas', variant: 'success' });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.error || err?.message,
        variant: 'destructive',
      });
    } finally {
      setSavingLobs(false);
    }
  };

  const handleAddMembers = async () => {
    if (!id || pickedUserIds.size === 0) {
      toast({ title: 'Seleccioná al menos un usuario', variant: 'destructive' });
      return;
    }
    setAdding(true);
    const validUntilIso = addValidUntil ? new Date(addValidUntil).toISOString() : null;
    const coveringForId = addCoveringForId || null;
    const ids = Array.from(pickedUserIds);
    const failures: string[] = [];
    try {
      await Promise.all(
        ids.map(async (userId) => {
          try {
            await organizationService.addMember(id, {
              userId,
              validUntil: validUntilIso,
              coveringForId,
            });
          } catch (err: any) {
            const u = allUsers.find((x) => x.id === userId);
            failures.push(
              `${u ? `${u.firstName} ${u.lastName}` : userId}: ${err?.response?.data?.error || err?.message}`
            );
          }
        })
      );
      const m = await organizationService.listMembers(id);
      setMembers(m);
      if (failures.length === ids.length) {
        toast({
          title: 'No se pudieron agregar miembros',
          description: failures[0],
          variant: 'destructive',
        });
      } else {
        toast({
          title:
            failures.length > 0
              ? `${ids.length - failures.length} agregados, ${failures.length} con error`
              : `${ids.length} miembros agregados`,
          variant: failures.length > 0 ? 'destructive' : 'success',
        });
        setAddOpen(false);
        setPickedUserIds(new Set());
        setUserSearch('');
        setAddValidUntil('');
        setAddCoveringForId('');
      }
    } finally {
      setAdding(false);
    }
  };

  const togglePickedUser = (userId: string) => {
    setPickedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Con el diálogo de la app y no el `confirm()` del navegador (la ventanita gris).
  const handleRemoveMember = (userId: string) => {
    if (!id) return;
    const m = members.find((x) => x.userId === userId);
    confirm({
      title: m
        ? `¿Quitar a ${m.firstName} ${m.lastName} del equipo?`
        : '¿Quitar este miembro del equipo?',
      description: 'Deja de recibir lo que se comparta a este equipo. Sigue en la empresa.',
      confirmText: 'Sí, quitar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await organizationService.removeMember(id, userId);
          setMembers((prev) => prev.filter((x) => x.userId !== userId));
          toast({ title: 'Miembro quitado', variant: 'success' });
        } catch (err: any) {
          toast({ title: 'Error', description: err?.message, variant: 'destructive' });
        }
      },
    });
  };

  const availableUsers = useMemo(
    () => allUsers.filter((u) => !members.some((m) => m.userId === u.id)),
    [allUsers, members]
  );

  const filteredAvailableUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return availableUsers;
    return availableUsers.filter((u) =>
      `${u.firstName} ${u.lastName} ${u.email} ${u.role}`.toLowerCase().includes(q)
    );
  }, [availableUsers, userSearch]);

  const filteredLobs = useMemo(() => {
    const q = lobSearch.trim().toLowerCase();
    if (!q) return allLobs;
    return allLobs.filter((l) => `${l.name} ${l.code}`.toLowerCase().includes(q));
  }, [allLobs, lobSearch]);

  const memberOptions = useMemo(
    () =>
      members.map((m) => ({
        value: m.userId,
        label: `${m.firstName} ${m.lastName} — ${m.email}`,
      })),
    [members]
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-[1440px] space-y-4 px-4 py-8 md:px-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!org) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-[1440px] px-4 py-8 text-muted-foreground md:px-8">
          Equipo no encontrado
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <ConfirmDialog />
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/* El encabezado de Mi empresa (rediseño del 18-oct), con la vuelta a Equipos. */}
        <PageHeader
          volver={{ texto: 'Equipos', onClick: () => navigate('/configuracion/equipos') }}
          etiqueta="Mi empresa · Equipo"
          icono={Building2}
          tonoEtiqueta="neutro"
          titulo={org.name}
          subtitulo={
            <>
              {org.description && <span className="mr-2">{org.description}</span>}
              <span className="font-mono text-xs">{org.code}</span>
            </>
          }
          acciones={
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1">
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                {members.length} {members.length === 1 ? 'miembro' : 'miembros'}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1">
                <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                {orgLobs.length} {orgLobs.length === 1 ? 'LOB' : 'LOBs'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => borrarEquipo(org.name)}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Borrar equipo
              </Button>
            </div>
          }
        />

        <Tabs defaultValue="lobs">
          <TabsList>
            <TabsTrigger value="lobs">
              <Layers className="w-4 h-4 mr-1.5" /> LOBs
            </TabsTrigger>
            <TabsTrigger value="members">
              <Users className="w-4 h-4 mr-1.5" /> Miembros
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lobs" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Líneas de negocio</CardTitle>
                    {/*
                      🔴 Decía que las cotizaciones de esas LOBs iban a ser visibles
                      para todos los miembros. No es así: `OrganizationLOB` se lee y
                      se escribe SÓLO en su propio CRUD (`organizations.routes.ts`)
                      y no entra en ningún filtro de ninguna consulta del backend.
                      Buscado en todo `packages/backend/src`.

                      Se deja la pantalla —el dato sirve para saber de qué se ocupa
                      cada equipo— pero el texto dice qué hace de verdad. Prometer
                      un permiso que no se aplica es lo que hace que alguien crea
                      que reparte accesos cuando no reparte nada.
                    */}
                    <CardDescription>
                      De qué líneas de negocio se ocupa este equipo. Es para tenerlo anotado y
                      organizar el trabajo: hoy no reparte accesos por sí solo — las cotizaciones se
                      comparten una por una, o al equipo entero, desde la cotización.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleSaveLobs}
                    disabled={!lobIdsChanged || savingLobs}
                    size="sm"
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    Guardar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {allLobs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No hay LOBs cargadas. Creá líneas de negocio en Master Data primero.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={lobSearch}
                          onChange={(e) => setLobSearch(e.target.value)}
                          placeholder="Buscar LOB por nombre o código…"
                          className="pl-9"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingLobIds(new Set(filteredLobs.map((l) => l.id)))}
                        >
                          Seleccionar todos
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingLobIds(new Set())}
                        >
                          Limpiar
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {filteredLobs.map((lob) => {
                        const checked = pendingLobIds.has(lob.id);
                        return (
                          <label
                            key={lob.id}
                            className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                              checked
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:bg-muted/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleLob(lob.id)}
                              className="w-4 h-4"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{lob.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{lob.code}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              L{lob.level}
                            </Badge>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Miembros del equipo</CardTitle>
                    {/*
                      🔴 Este texto decía «Cualquier miembro ve las cotizaciones del
                      equipo (incluso quotes de otros miembros)» y era falso.

                      La regla que rige hoy (`quote-visibility.ts`, fijada el
                      8-sep) es la contraria: la cotización es privada de quien la
                      creó, y estar en el equipo NO da acceso a nada. El equipo es
                      a QUIÉN se le puede compartir de una sola vez. Medido: un
                      vendedor metido al equipo del autor sigue sin ver la
                      cotización hasta que alguien la comparte.

                      Un cartel que promete visibilidad que no existe es peor que
                      no tener cartel: manda a un admin a resolver un permiso
                      agregando gente a un equipo, y nada cambia.
                    */}
                    <CardDescription>
                      Estar en el equipo no da acceso a las cotizaciones de los demás. Sirve para lo
                      contrario: quien tiene una cotización puede compartirla{' '}
                      <strong>al equipo entero</strong> desde la cotización misma, y les llega a
                      todos los que estén acá. Para cubrir vacaciones, agregá a la persona con{' '}
                      <em>Cobertura temporal</em> y una fecha de fin.
                    </CardDescription>
                  </div>
                  <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <Button size="sm" onClick={() => setAddOpen(true)}>
                      <Plus className="w-4 h-4 mr-1.5" />
                      Agregar miembro
                    </Button>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Agregar miembros al equipo</DialogTitle>
                        <DialogDescription>
                          Seleccioná uno o varios usuarios del listado. Si es una cobertura de
                          vacaciones, definí la fecha de fin abajo (aplica a todos los
                          seleccionados).
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 py-2">
                        <div>
                          <label className="text-sm font-medium mb-1.5 block">
                            Usuarios disponibles ({filteredAvailableUsers.length})
                          </label>
                          <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              placeholder="Buscar por nombre, email o rol…"
                              className="pl-9"
                            />
                          </div>
                          <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
                            {filteredAvailableUsers.length === 0 ? (
                              <p className="text-xs text-muted-foreground p-4 text-center">
                                {availableUsers.length === 0
                                  ? 'Todos los usuarios ya son miembros del equipo.'
                                  : 'Sin resultados para tu búsqueda.'}
                              </p>
                            ) : (
                              filteredAvailableUsers.map((u) => {
                                const checked = pickedUserIds.has(u.id);
                                return (
                                  <label
                                    key={u.id}
                                    className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-muted/50 ${
                                      checked ? 'bg-primary/5' : ''
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => togglePickedUser(u.id)}
                                      className="w-4 h-4"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium">
                                        {u.firstName} {u.lastName}
                                      </p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {u.email}
                                      </p>
                                    </div>
                                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                                      {nombreDelRol(u.role)}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          {pickedUserIds.size > 0 && (
                            <p className="text-xs text-muted-foreground mt-1.5">
                              {pickedUserIds.size} seleccionado
                              {pickedUserIds.size === 1 ? '' : 's'}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t">
                          <div>
                            <label className="text-sm font-medium">Cobertura temporal hasta</label>
                            <Input
                              type="date"
                              value={addValidUntil}
                              onChange={(e) => setAddValidUntil(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Vacío = permanente.
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium">Cubre a</label>
                            <SearchableSelect
                              options={[{ value: '', label: '— Nadie —' }, ...memberOptions]}
                              value={addCoveringForId}
                              onValueChange={(v) => setAddCoveringForId(v)}
                              placeholder="Seleccionar miembro…"
                              searchPlaceholder="Buscar…"
                              emptyMessage="Sin miembros aún"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Solo informativo (vacaciones).
                            </p>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)}>
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleAddMembers}
                          disabled={adding || pickedUserIds.size === 0}
                        >
                          Agregar {pickedUserIds.size > 0 ? `${pickedUserIds.size} ` : ''}
                          miembro{pickedUserIds.size === 1 ? '' : 's'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {members.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    Sin miembros. Agregá el primero arriba.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Persona</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Acceso</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members.map((m) => {
                        const isCoverage = !!m.validUntil;
                        const isExpired = m.validUntil && new Date(m.validUntil) < new Date();
                        return (
                          <TableRow key={m.userId}>
                            <TableCell className="py-3">
                              {/* Como la lista de Usuarios: la cara, el nombre y el correo debajo. */}
                              <div className="flex items-center gap-3">
                                <AvatarDePersona
                                  nombre={`${m.firstName} ${m.lastName}`}
                                  clave={m.userId}
                                />
                                <div className="min-w-0">
                                  <p className="truncate font-semibold leading-tight">
                                    {m.firstName} {m.lastName}
                                  </p>
                                  <p className="truncate text-[13px] text-muted-foreground">
                                    {m.email}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                                {nombreDelRol(m.role)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {isCoverage ? (
                                <div className="flex items-center gap-1 text-xs">
                                  <CalendarClock
                                    className={`w-3 h-3 ${isExpired ? 'text-red-500' : 'text-amber-500'}`}
                                  />
                                  <span className={isExpired ? 'text-red-600' : 'text-amber-600'}>
                                    Hasta {new Date(m.validUntil!).toLocaleDateString('es-BO')}
                                  </span>
                                  {m.coveringFor && (
                                    <span className="text-muted-foreground">
                                      · cubre a {m.coveringFor.firstName}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">Permanente</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMember(m.userId)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}

export default EquipoDetailPage;
